import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Tooltip,
} from '@apollo/orbit';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import Config from 'src/lib/config';
import { truncateString } from 'src/lib/connectors/helpers';
import { mergeQueryParams } from 'src/lib/routing';

import { ConnectorsDebuggingResult } from './shared';
import { Request, RequestList } from './views/RequestList';
import { ResponseTabs } from './views/ResponseTabs';

const MAX_BREADCRUMB_LENGTH = 30;

export interface Props {
  version: ConnectorsDebuggingResult['version'];
  data: ConnectorsDebuggingResult['data'];
}

const SUPPORTED_PAYLOAD_VERSIONS = ['1'];

export const ConnectorsResponsePane = ({ version, data = [] }: Props) => {
  const [selectedRowIdx, setSelectedRowIdx] = useState<number>(-1);
  const [showMappingErrorFirst, setShowMappingErrorFirst] = useState(false);

  useEffect(() => {
    setSelectedRowIdx(-1);
  }, [data]);

  const requestListData: Request[] = useMemo(() => {
    return (
      data?.map(({ request, response }, idx) => {
        const url = new URL(request?.url ?? '');

        return {
          url: request?.url ?? '',
          path: url.pathname + url.search,
          method: request?.method ?? '',
          status: response?.status,
          selectionErrorsCount: response?.body?.selection?.errors?.length ?? 0,
          idx,
          id: idx + 1,
        };
      }) ?? []
    );
  }, [data]);

  if (!SUPPORTED_PAYLOAD_VERSIONS.includes(version)) {
    return (
      <Alert status="info" className="mt-5">
        Please{' '}
        <Link
          className="underline"
          to={(location) => ({
            ...location,
            search: mergeQueryParams(location.search, {
              [Config.queryParameters.Overlay]: Config.modals.appChangelog,
            }),
          })}
        >
          update Sandbox
        </Link>{' '}
        to view Connectors debugging data.
      </Alert>
    );
  }

  const rowSelected = selectedRowIdx > -1;
  const selectedRowData = data[selectedRowIdx];
  const selectedRowID = rowSelected
    ? requestListData[selectedRowIdx]?.path
    : '';

  return (
    <>
      <Breadcrumb className="my-4">
        <BreadcrumbItem isCurrentPage={!rowSelected}>
          <BreadcrumbLink
            onClick={() => {
              setSelectedRowIdx(-1);
            }}
          >
            All requests
          </BreadcrumbLink>
        </BreadcrumbItem>
        {rowSelected && (
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>
              {selectedRowID && selectedRowID.length > MAX_BREADCRUMB_LENGTH ? (
                <Tooltip label={selectedRowID}>
                  {truncateString(selectedRowID, MAX_BREADCRUMB_LENGTH)}
                </Tooltip>
              ) : (
                selectedRowID
              )}
            </BreadcrumbLink>
          </BreadcrumbItem>
        )}
      </Breadcrumb>
      <>
        {rowSelected ? (
          <ResponseTabs
            data={selectedRowData}
            showMappingErrorFirst={showMappingErrorFirst}
          />
        ) : (
          <RequestList
            requests={requestListData}
            onRowClick={(rowIdx, showErrorsFirst = false) => {
              setSelectedRowIdx(rowIdx);
              if (showErrorsFirst !== showMappingErrorFirst) {
                setShowMappingErrorFirst(showErrorsFirst);
              }
            }}
          />
        )}
      </>
    </>
  );
};
